import { useEffect, useState } from "react";
import {
  getCurrentServerInstant,
  getServerTimezone,
} from "@/shared/utils/serverTimeContext";

/**
 * Server-time "phase of day" — a continuous gauge of how far the
 * authoritative server-timezone clock has progressed through the
 * current 24-hour day, plus a coarse day/night flag.
 *
 *   dayProgress  0 .. 1   (0 = midnight, 0.5 = noon, ~1 = next midnight)
 *   isDay        boolean  (true between 06:00 and 18:00 in server tz)
 *
 * This is intentionally separate from `AppearanceProvider`'s
 * light/dark THEME — those describe the *user's chosen UI theme*;
 * `useServerPhase` describes the *natural time of day at the
 * operations location*. Both can exist simultaneously: a user in a
 * dark theme at 14:00 EST has `theme = "dark"` AND `isDay = true`.
 *
 * Used by:
 *   • `Navbar` to apply an ambient day/night background tint on the
 *     header chrome (sunlight warm overlay during the day, cool
 *     moonlight overlay at night). The tint is purely decorative —
 *     no semantic meaning, no clickable behaviour — so it doesn't
 *     conflict with the AppearanceToggle's theme sun/moon glyphs.
 *
 * Tick cadence: 60 s. The day/night flag flips only at the 06:00 /
 * 18:00 boundaries; the navbar's CSS `transition: opacity` on the
 * day/night overlay pseudo-elements smooths those flips into a slow
 * crossfade. `dayProgress` updates coarsely enough for any UI gauge
 * that wants to consume it.
 */
const HMS_FORMATTER_CACHE = new Map<string, Intl.DateTimeFormat>();

const getHmsFormatter = (timeZone: string): Intl.DateTimeFormat => {
  const cached = HMS_FORMATTER_CACHE.get(timeZone);
  if (cached) return cached;
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  });
  HMS_FORMATTER_CACHE.set(timeZone, formatter);
  return formatter;
};

const getServerHms = (): { h: number; m: number; s: number } => {
  const parts = getHmsFormatter(getServerTimezone()).formatToParts(
    getCurrentServerInstant(),
  );
  const h = Number(parts.find((p) => p.type === "hour")?.value ?? "0");
  const m = Number(parts.find((p) => p.type === "minute")?.value ?? "0");
  const s = Number(parts.find((p) => p.type === "second")?.value ?? "0");
  return { h, m, s };
};

const DAY_START_HOUR = 6;  // inclusive
const DAY_END_HOUR   = 18; // exclusive — flips back to night at 18:00

const computePhase = (): { dayProgress: number; isDay: boolean } => {
  const { h, m, s } = getServerHms();
  const seconds = h * 3600 + m * 60 + s;
  const dayProgress = Math.max(0, Math.min(1, seconds / 86_400));
  const isDay = h >= DAY_START_HOUR && h < DAY_END_HOUR;
  return { dayProgress, isDay };
};

export interface ServerPhase {
  /** [0, 1] — fraction of the current server-tz day elapsed. */
  dayProgress: number;
  /** True between 06:00 and 18:00 (inclusive / exclusive) in server tz. */
  isDay: boolean;
}

export const useServerPhase = (): ServerPhase => {
  const [phase, setPhase] = useState<ServerPhase>(computePhase);

  useEffect(() => {
    // 60 s is fine — `isDay` only flips twice a day, and `dayProgress`
    // moves slowly enough that minute granularity covers any UI use.
    const tick = (): void => setPhase(computePhase());
    tick();
    const id = setInterval(tick, 60_000);
    return () => clearInterval(id);
  }, []);

  return phase;
};
